import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

// Create the context
const PartnerStatusContext = createContext();

// Icon mapping for dynamic icon rendering
const ICON_COMPONENTS = {
  'FileText': 'FeatherFileText',
  'Users': 'FeatherUsers',
  'AlertCircle': 'FeatherAlertCircle',
  'Clock': 'FeatherClock',
  'Activity': 'FeatherActivity',
  'Flag': 'FeatherFlag',
  'CheckCircle': 'FeatherCheckCircle',
  'RefreshCw': 'FeatherRefreshCw',
  'X': 'FeatherX',
  'ArrowLeft': 'FeatherArrowLeft',
  'Calendar': 'FeatherCalendar',
  'Trash2': 'FeatherTrash2'
};

/**
 * PartnerStatusProvider - Manages partner company status configurations
 * Fetches data once after login and caches it for the session
 */
export const PartnerStatusProvider = ({ children }) => {
  const { user } = useAuth();
  const [statuses, setStatuses] = useState([]);
  const [statusMap, setStatusMap] = useState({}); // For O(1) lookups
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);

  /**
   * Fetch partner company statuses from the API
   */
  const fetchStatuses = async () => {
    if (!user) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/api/partner-statuses');
      
      if (response.data.success) {
        const { statuses: statusData } = response.data.data;
        
        // Create status map for fast lookups
        const map = {};
        statusData.forEach(status => {
          map[status.status_key] = status;
        });
        
        setStatuses(statusData);
        setStatusMap(map);
        setLastFetch(new Date().toISOString());
      } else {
        throw new Error(response.data.error || 'Failed to fetch partner statuses');
      }
    } catch (err) {
      console.error('❌ Error fetching partner statuses:', err);
      setError(err.message);
      
      // Set empty data to prevent crashes
      setStatuses([]);
      setStatusMap({});
    } finally {
      setLoading(false);
    }
  };

  // Fetch statuses when user logs in
  useEffect(() => {
    if (user && statuses.length === 0) {
      fetchStatuses();
    }
  }, [user]);

  /**
   * Get status configuration by status key
   */
  const getStatusConfig = (statusKey) => {
    if (!statusKey) return null;
    return statusMap[statusKey] || null;
  };

  /**
   * Get status color (replaces hardcoded getStatusColor function)
   */
  const getStatusColor = (statusKey) => {
    const config = getStatusConfig(statusKey);
    return config?.color_variant || 'neutral';
  };

  /**
   * Get status icon name (replaces hardcoded getStatusIcon function)
   */
  const getStatusIcon = (statusKey) => {
    const config = getStatusConfig(statusKey);
    return config?.icon_name || 'FileText';
  };

  /**
   * Get full icon component name for importing
   */
  const getStatusIconComponent = (statusKey) => {
    const iconName = getStatusIcon(statusKey);
    return ICON_COMPONENTS[iconName] || 'FeatherFileText';
  };

  /**
   * Format status for display (replaces hardcoded formatStatus function)
   */
  const formatStatus = (statusKey) => {
    const config = getStatusConfig(statusKey);
    return config?.display_name || 
           statusKey?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 
           'Unknown';
  };

  /**
   * Check if a status is final (cannot transition from)
   */
  const isStatusFinal = (statusKey) => {
    const config = getStatusConfig(statusKey);
    return config?.is_final || false;
  };

  /**
   * Check if quick log should be highlighted for this status
   */
  const shouldHighlightQuickLog = (statusKey) => {
    const config = getStatusConfig(statusKey);
    return config?.highlight_quick_log || false;
  };

  /**
   * Check if company editing is allowed in this status
   */
  const allowEditing = (statusKey) => {
    const config = getStatusConfig(statusKey);
    return config?.allow_editing !== false; // Default to true if not specified
  };

  /**
   * Get workflow stage for a status
   */
  const getWorkflowStage = (statusKey) => {
    const config = getStatusConfig(statusKey);
    return config?.workflow_stage || 'initial';
  };

  /**
   * Get statuses for dropdown options (ordered by sequence)
   */
  const getStatusOptions = () => {
    return statuses.map(status => ({
      value: status.status_key,
      label: status.display_name,
      color: status.color_variant,
      icon: status.icon_name,
      stage: status.workflow_stage,
      isFinal: status.is_final,
      sequence: status.sequence_order
    }));
  };

  /**
   * Get available status transitions for a given current status
   * This implements business logic for status progression
   */
  const getAvailableTransitions = (currentStatus) => {
    if (!currentStatus) return [];
    
    const currentConfig = getStatusConfig(currentStatus);
    if (!currentConfig) return [];
    
    // If it's a final status, no transitions allowed
    if (currentConfig.is_final) return [];
    
    // Define rejection statuses that should always be available for transitions
    const rejectionStatuses = ['not_eligible', 'not_interested'];
    
    // Get all available statuses (excluding current status, but including rejection statuses)
    const availableStatuses = statuses.filter(status => 
      status.status_key !== currentStatus && 
      status.is_active !== false &&
      // Include rejection statuses even if they're marked as final
      (!status.is_final || rejectionStatuses.includes(status.status_key))
    );
    
    // Apply business logic for transitions
    switch (currentStatus) {
      case 'initial_data_entered':
        // Can only move to contacted
        return availableStatuses.filter(status => status.status_key === 'contacted');
        
      case 'contacted':
        // Can move to contacted_no_response, in_discussion, or rejection paths
        return availableStatuses.filter(status => 
          ['contacted_no_response', 'in_discussion', 'not_eligible', 'not_interested'].includes(status.status_key)
        );
        
      case 'contacted_no_response':
        // Can move to in_discussion or rejection paths
        return availableStatuses.filter(status => 
          ['in_discussion', 'not_eligible', 'not_interested'].includes(status.status_key)
        );
        
      case 'in_discussion':
        // Can move to ready_to_engage or rejection paths
        return availableStatuses.filter(status => 
          ['ready_to_engage', 'not_eligible', 'not_interested'].includes(status.status_key)
        );
        
      case 'ready_to_engage':
        // Can move to onboarding_in_progress or rejection paths
        return availableStatuses.filter(status => 
          ['onboarding_in_progress', 'not_eligible', 'not_interested'].includes(status.status_key)
        );
        
      case 'onboarding_in_progress':
        // Can move to onboarding_complete or rejection paths
        return availableStatuses.filter(status => 
          ['onboarding_complete', 'not_eligible', 'not_interested'].includes(status.status_key)
        );
        
      case 'onboarding_complete':
        // Auto-transitions to fully_onboarded, but show both options
        return availableStatuses.filter(status => 
          ['fully_onboarded'].includes(status.status_key)
        );
        
      case 'awaiting_documents':
        // Legacy status - can move to onboarding_complete or rejection paths
        return availableStatuses.filter(status => 
          ['onboarding_complete', 'not_eligible', 'not_interested'].includes(status.status_key)
        );
        
      default:
        // For other statuses, allow transitions to any non-final, active status
        return availableStatuses;
    }
  };

  /**
   * Get statuses by workflow stage
   */
  const getStatusesByStage = (stage) => {
    return statuses.filter(status => status.workflow_stage === stage);
  };

  /**
   * Get initial statuses (can be first status for new companies)
   */
  const getInitialStatuses = () => {
    return statuses.filter(status => status.is_initial);
  };

  /**
   * Get final statuses (cannot transition from)
   */
  const getFinalStatuses = () => {
    return statuses.filter(status => status.is_final);
  };

  /**
   * Refresh status configurations (useful for admin changes)
   */
  const refreshStatuses = () => {
    setStatuses([]);
    setStatusMap({});
    fetchStatuses();
  };

  // Context value
  const value = {
    // Data
    statuses,
    statusMap,
    loading,
    error,
    lastFetch,
    isReady: !loading && !error && statuses.length > 0,

    // Core utility functions (replace hardcoded functions)
    getStatusConfig,
    getStatusColor,
    getStatusIcon,
    getStatusIconComponent,
    formatStatus,

    // Status properties
    isStatusFinal,
    shouldHighlightQuickLog,
    allowEditing,
    getWorkflowStage,

    // Status collections
    getStatusOptions,
    getStatusesByStage,
    getInitialStatuses,
    getFinalStatuses,
    getAvailableTransitions,

    // Actions
    refreshStatuses,
    fetchStatuses
  };

  return (
    <PartnerStatusContext.Provider value={value}>
      {children}
    </PartnerStatusContext.Provider>
  );
};

/**
 * Hook to use partner status context
 */
export const usePartnerStatus = () => {
  const context = useContext(PartnerStatusContext);
  if (!context) {
    throw new Error('usePartnerStatus must be used within a PartnerStatusProvider');
  }
  return context;
};

/**
 * Backward compatibility hook - provides the same interface as hardcoded functions
 * This allows existing components to work without changes
 */
export const usePartnerStatusCompat = () => {
  const partnerStatus = usePartnerStatus();
  
  // Hardcoded status options as fallback (same logic as CompanyDetailView)
  const getHardcodedStatusOptions = (currentStatus) => {
    switch (currentStatus) {
      case 'initial_data_entered':
        return [
          { value: 'initial_data_entered', label: 'New' },
          { value: 'contacted', label: 'Contacted' }
        ];
      
      case 'contacted':
        return [
          { value: 'contacted', label: 'Contacted' }
        ];
      
      case 'contacted_no_response':
        return [
          { value: 'contacted_no_response', label: 'Contacted - No Response' },
          { value: 'in_discussion', label: 'In Discussion' },
          { value: 'not_eligible', label: 'Not Eligible' },
          { value: 'not_interested', label: 'Not Interested' }
        ];
      
      case 'in_discussion':
        return [
          { value: 'in_discussion', label: 'In Discussion' },
          { value: 'ready_to_engage', label: 'Ready to Engage' },
          { value: 'not_eligible', label: 'Not Eligible' },
          { value: 'not_interested', label: 'Not Interested' }
        ];
      
      case 'ready_to_engage':
        return [
          { value: 'ready_to_engage', label: 'Ready to Engage' },
          { value: 'onboarding_in_progress', label: 'Onboarding in Progress' },
          { value: 'not_eligible', label: 'Not Eligible' },
          { value: 'not_interested', label: 'Not Interested' }
        ];
      
      case 'onboarding_in_progress':
        return [
          { value: 'onboarding_in_progress', label: 'Onboarding in Progress' },
          { value: 'onboarding_complete', label: 'Onboarding Complete' },
          { value: 'not_eligible', label: 'Not Eligible' },
          { value: 'not_interested', label: 'Not Interested' }
        ];
      
      case 'onboarding_complete':
        return [
          { value: 'onboarding_complete', label: 'Onboarding Complete' },
          { value: 'fully_onboarded', label: 'Fully Onboarded' }
        ];
      
      case 'awaiting_documents':
        return [
          { value: 'awaiting_documents', label: 'Awaiting Documents' },
          { value: 'onboarding_complete', label: 'Onboarding Complete' },
          { value: 'not_eligible', label: 'Not Eligible' },
          { value: 'not_interested', label: 'Not Interested' }
        ];
      
      case 'fully_onboarded':
        return [
          { value: 'fully_onboarded', label: 'Fully Onboarded' }
        ];
      
      case 'agreement_signed':
        return [
          { value: 'agreement_signed', label: 'Agreement Signed' }
        ];
      
      case 'rejected_not_interested':
        return [
          { value: 'rejected_not_interested', label: 'Rejected/Not Interested' }
        ];
      
      case 'not_eligible':
        return [
          { value: 'not_eligible', label: 'Not Eligible' }
        ];
      
      case 'not_interested':
        return [
          { value: 'not_interested', label: 'Not Interested' }
        ];
      
      default:
        return [
          { value: currentStatus, label: currentStatus }
        ];
    }
  };
  
  // Return functions with the same signature as before
  return {
    getStatusColor: partnerStatus.getStatusColor,
    getStatusIcon: partnerStatus.getStatusIcon,
    getStatusIconComponent: partnerStatus.getStatusIconComponent,
    formatStatus: partnerStatus.formatStatus,
    
    // Add the missing methods that PartnerCompanyCard expects
    getStatusConfig: partnerStatus.getStatusConfig,
    getAvailableTransitions: partnerStatus.getAvailableTransitions,
    
    // For dropdowns - return properly formatted options with current status and available transitions
    getAvailableStatusOptions: (currentStatus) => {
      if (!partnerStatus.isReady) {
        // Fallback to hardcoded options when database system isn't ready
        return getHardcodedStatusOptions(currentStatus);
      }
      
      const transitions = partnerStatus.getAvailableTransitions(currentStatus);
      
      // If no transitions found, fall back to hardcoded options
      if (!transitions || transitions.length === 0) {
        return getHardcodedStatusOptions(currentStatus);
      }
      
      // Include current status as first option (selected by default)
      const currentStatusConfig = partnerStatus.getStatusConfig(currentStatus);
      
      const currentOption = {
        value: currentStatus,
        label: currentStatusConfig?.display_name || currentStatus
      };
      
      // Format transitions to match expected structure
      const formattedTransitions = transitions.map(status => ({
        value: status.status_key,
        label: status.display_name || status.status_key
      }));
      
      // Combine current status with available transitions, removing duplicates
      const allOptions = [currentOption];
      formattedTransitions.forEach(transition => {
        if (!allOptions.some(opt => opt.value === transition.value)) {
          allOptions.push(transition);
        }
      });
      
      return allOptions;
    },
    
    isReady: partnerStatus.isReady,
    loading: partnerStatus.loading,
    error: partnerStatus.error
  };
};

export default PartnerStatusContext; 